import CustomerServiceAPI from '@/api/customer-services.api'

// Holds the super-admin impersonation target list. The whole slim account list (< 1k rows) is fetched
// and filtered client-side — there is no server-side search and no impersonation "session" here
// (entering/leaving impersonation is just switching the current account). The list is cached for
// CACHE_TTL_MS and re-fetched on next access once stale.
const CACHE_TTL_MS = 10 * 60 * 1000

const state = {
  accounts: [],
  isLoading: false,
  hasLoaded: false,
  loadError: false,
  loadedAt: 0
}

/**
 * @type {import('vuex').GetterTree<typeof state>}
 */
const getters = {
  impersonationAccounts(state) {
    return state.accounts
  },

  isLoadingImpersonationAccounts(state) {
    return state.isLoading
  },

  impersonationAccountsLoaded(state) {
    return state.hasLoaded
  },

  impersonationAccountsLoadError(state) {
    return state.loadError
  }
}

/**
 * @type {import('vuex').ActionTree<typeof state>}
 */
const actions = {
  // Cached fetch: returns immediately if the cached list is still fresh (within CACHE_TTL_MS),
  // unless `force` (manual refresh) is passed.
  async fetchImpersonationAccounts({ commit, state }, { force = false } = {}) {
    const isFresh = Date.now() - state.loadedAt < CACHE_TTL_MS

    if (state.hasLoaded && isFresh && !force) {
      return
    }

    commit('SET_LOADING', true)
    commit('SET_LOAD_ERROR', false)

    try {
      const response = await CustomerServiceAPI.getImpersonationAccounts()

      commit('SET_ACCOUNTS', response.data || [])
      commit('SET_HAS_LOADED', true)
      commit('SET_LOADED_AT', Date.now())
    } catch (error) {
      console.error(error)
      commit('SET_LOAD_ERROR', true)
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const mutations = {
  SET_ACCOUNTS(state, accounts) {
    state.accounts = accounts
  },

  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading
  },

  SET_HAS_LOADED(state, hasLoaded) {
    state.hasLoaded = hasLoaded
  },

  SET_LOADED_AT(state, loadedAt) {
    state.loadedAt = loadedAt
  },

  SET_LOAD_ERROR(state, loadError) {
    state.loadError = loadError
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
